import React from 'react';
import {Box, Stack, Typography} from "@mui/material";
import {CheckRounded} from "@mui/icons-material";

export const SCAN_STEPS = ['Capture', 'Review', 'Destination', 'Split', 'Done'];

interface ScanProgressProps {
  /** Zero-based index of the current step. */
  activeStep: number;
  /** Steps to render. Defaults to the full scan flow. */
  steps?: string[];
}

/**
 * Lightweight numbered step indicator for the scan / manual receipt flow.
 * Purely presentational — it never drives state, so form values are preserved
 * across steps.
 */
const ScanProgress = ({activeStep, steps = SCAN_STEPS}: ScanProgressProps) => {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      role="list"
      aria-label={`Step ${activeStep + 1} of ${steps.length}`}
      sx={{mb: 1}}
    >
      {steps.map((label, index) => {
        const done = index < activeStep;
        const current = index === activeStep;
        return (
          <Stack key={label} alignItems="center" spacing={0.5} role="listitem" sx={{flex: 1, minWidth: 0}}>
            <Box
              aria-current={current ? 'step' : undefined}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
                bgcolor: done || current ? '#1a2035' : '#e5e7eb',
                color: done || current ? '#fff' : '#6b7280',
                border: current ? '2px solid #6366f1' : '2px solid transparent',
              }}
            >
              {done ? <CheckRounded sx={{fontSize: 16}}/> : index + 1}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: current ? '#1a2035' : '#9ca3af',
                fontWeight: current ? 700 : 500,
                textAlign: 'center',
                lineHeight: 1.1,
              }}
            >
              {label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ScanProgress;
